import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, Smartphone, CheckCircle, ArrowRight, Share2, Clock, MapPin, Camera, Shield, Plus } from 'lucide-react';

export default function InstallAppPage() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Verificar se já está instalado (modo standalone)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    
    if (isStandalone) {
      // Se já está instalado, redireciona direto para o login
      navigate('/login', { replace: true });
      return;
    }

    // Verificar se é iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Verificar se é Android
    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    // Capturar evento de instalação (Android Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Verificar se foi instalado
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [navigate]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  const handleOpenApp = () => {
    navigate('/login');
  };

  // Se já está instalado, mostrar mensagem de sucesso
  if (isInstalled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">App Instalado!</h1>
            <p className="text-slate-600 mb-6">
              O CiaPonto foi instalado com sucesso. Redirecionando para o login...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900">
      {/* Header */}
      <div className="pt-8 pb-6 px-4 text-center">
        <div className="flex justify-center mb-3">
          <img 
            src="/logo-control-isp.png" 
            alt="CiaPonto" 
            className="h-16 w-auto brightness-0 invert"
          />
        </div>
        <p className="text-sky-200 text-sm">Sistema de Ponto Eletrônico</p>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-8">
        <Card className="w-full max-w-md mx-auto border-0 shadow-2xl">
          <CardContent className="p-5">
            {/* Título */}
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Smartphone className="w-7 h-7 text-sky-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 mb-1">Instale o App</h1>
              <p className="text-sm text-slate-500">Acesse rápido pelo celular</p>
            </div>

            {/* Recursos resumidos */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Clock className="w-4 h-4 text-sky-500" />
                <span className="text-xs text-slate-700">Ponto Rápido</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <MapPin className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-700">GPS Automático</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Camera className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-slate-700">Foto de Prova</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Shield className="w-4 h-4 text-orange-500" />
                <span className="text-xs text-slate-700">Modo Offline</span>
              </div>
            </div>

            {/* Botão principal de instalação */}
            {deferredPrompt ? (
              <Button 
                onClick={handleInstall}
                className="w-full bg-sky-500 hover:bg-sky-600 h-12 text-base font-semibold mb-3"
              >
                <Download className="w-5 h-5 mr-2" />
                Instalar Agora
              </Button>
            ) : (
              <Button 
                onClick={() => setShowInstructions(true)}
                className="w-full bg-sky-500 hover:bg-sky-600 h-12 text-base font-semibold mb-3"
              >
                <Download className="w-5 h-5 mr-2" />
                Como Instalar
              </Button>
            )}

            {/* Instruções de instalação */}
            {showInstructions && (
              <div className="bg-slate-50 rounded-xl p-4 mb-3 animate-in slide-in-from-top-2">
                {isIOS ? (
                  <div className="space-y-3">
                    <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      <span className="text-lg">🍎</span> No iPhone/iPad (Safari):
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <p className="text-xs text-slate-700">Toque em <Share2 className="w-3 h-3 inline mx-1" /> <strong>Compartilhar</strong> (barra inferior)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <p className="text-xs text-slate-700">Selecione <Plus className="w-3 h-3 inline mx-1" /> <strong>"Adicionar à Tela de Início"</strong></p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <p className="text-xs text-slate-700">Toque em <strong>"Adicionar"</strong></p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      <span className="text-lg">🤖</span> No Android (Chrome):
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <p className="text-xs text-slate-700">Toque nos <strong>3 pontinhos ⋮</strong> (canto superior)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <p className="text-xs text-slate-700">Selecione <strong>"Adicionar à tela inicial"</strong></p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-sky-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <p className="text-xs text-slate-700">Toque em <strong>"Adicionar"</strong> ou <strong>"Instalar"</strong></p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botão secundário */}
            <Button 
              onClick={handleOpenApp}
              variant="outline"
              className="w-full h-10 text-sm"
            >
              Continuar sem instalar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Dica */}
        <p className="text-center text-sky-200/60 text-xs mt-4 px-8">
          💡 Instalando o app, você acessa mais rápido e pode usar offline!
        </p>
      </div>
    </div>
  );
}
